import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from 'shared/hooks/useAuth';
import IncidentDialog, { emptyIncidentRow } from '../../shared/IncidentDialog';
import { apiErrorMessage } from '../../utils/responseHelpers';

/**
 * Create dialog for /system-register/incidents —
 * choose IT Asset (assigned devices) or Software, then enter incident details.
 * Compatible with EntityCrudPage CreateDialog props.
 */
export default function CreateIncidentDialog({
  open,
  onClose,
  onSuccess,
  createItem,
  softwareOptions = [],
}) {
  const { user } = useAuth();
  const reportingStaff = user?.data?.name?.trim() || '';
  const [saving, setSaving] = useState(false);

  const handleSave = async (values) => {
    const isAsset = values?.targetType === 'IT_ASSET';
    if (isAsset && !(values?.assetRegisterIds || []).length) {
      toast.error('Select at least one assigned device.');
      return;
    }
    if (!isAsset && !values?.softwareId) {
      toast.error('Select a software system to link this incident.');
      return;
    }
    setSaving(true);
    try {
      await createItem({
        ...values,
        targetType: values.targetType || 'SOFTWARE',
        softwareId: isAsset ? null : values.softwareId,
        assetRegisterIds: isAsset ? (values.assetRegisterIds || []) : [],
        resolutionStatus: values.resolutionStatus || 'OPEN',
      });
      toast.success(
        isAsset
          ? 'Incident created and linked to IT asset(s)'
          : 'Incident created and linked to software'
      );
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create incident.'));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return (
    <IncidentDialog
      open={open}
      mode="create"
      selectTargetType
      softwareOptions={softwareOptions}
      initialValues={emptyIncidentRow({
        incidentReportingStaff: reportingStaff,
        reportedBy: reportingStaff,
      })}
      defaultReportingStaff={reportingStaff}
      saving={saving}
      onClose={() => {
        if (saving) return;
        onClose?.();
      }}
      onSave={handleSave}
    />
  );
}
